import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';

const SiteContext = React.createContext(null);

export const useSiteContext = () => useContext(SiteContext);

export function useSite() {
    const { site = null } = useSiteContext() || {};
    return site || {};
}

export function useProjectionsSite() {
    const { sites = null } = useSiteContext() || {};
    return (sites || []).find((s) => s.handle === 'projections') || {};
}

export function useSemaineSite() {
    const { sites = null } = useSiteContext() || {};
    return (sites || []).find((s) => s.handle === 'semainedudoc') || {};
}

export function useEdition() {
    const { edition = null } = useSiteContext() || {};
    return edition || {};
}

export function useSize() {
    const { size = null } = useSiteContext() || {};
    return size || {};
}

export function useMenus() {
    const { menus = null } = useSiteContext() || {};
    return menus || null;
}

export function useCatalog() {
    const { catalog = null } = useSiteContext() || {};
    return catalog || null;
}

export function useMenuOpen() {
    const { menuOpen = null, setMenuOpen } = useSiteContext() || {};
    return { menuOpen, setMenuOpen };
}

export function usePopups() {
    const { popups = null } = useSiteContext() || {};
    return popups || null;
}

export function useCookies() {
    const { showCookies = null, openCookies = null, closeCookies = null } = useSiteContext() || {};
    return { showCookies, openCookies, closeCookies };
}

export const useHeader = () => {
    const {
        headerHidden,
        setHeaderHidden,
        scrollDirection,
        setScrollDirection,
        headerDisabled,
        setHeaderDisabled,
    } = useSiteContext();
    return {
        headerHidden,
        setHeaderHidden,
        scrollDirection,
        setScrollDirection,
        headerDisabled,
        setHeaderDisabled,
    };
};

const propTypes = {
    children: PropTypes.node.isRequired,
    site: PropTypes.shape({ handle: PropTypes.string }),
    sites: PropTypes.arrayOf(PropTypes.shape({ handle: PropTypes.string })),
    edition: PropTypes.shape({}),
    size: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
    menus: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    showCookies: PropTypes.bool,
    catalog: PropTypes.shape({}),
};

export const SiteProvider = ({
    children = null,
    site = null,
    sites = [],
    edition = null,
    size = null,
    menus = null,
    catalog = null,
    showCookies: initialShowCoookies = null,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [headerHidden, setHeaderHidden] = useState(false);
    const [headerDisabled, setHeaderDisabled] = useState(false);
    const [scrollDirection, setScrollDirection] = useState(null);
    const [showCookies, setShowCookies] = useState(initialShowCoookies || false);

    const closeCookies = useCallback(() => {
        setShowCookies(false);
    }, [setShowCookies]);

    const openCookies = useCallback(() => {
        setShowCookies(true);
    }, [setShowCookies]);

    const value = useMemo(
        () => ({
            site,
            sites,
            edition,
            size,
            menuOpen,
            setMenuOpen,
            menus,
            headerHidden,
            setHeaderHidden,
            headerDisabled,
            setHeaderDisabled,
            scrollDirection,
            setScrollDirection,
            showCookies,
            closeCookies,
            openCookies,
            catalog,
        }),
        [
            site,
            sites,
            edition,
            size,
            menuOpen,
            setMenuOpen,
            menus,
            headerHidden,
            setHeaderHidden,
            headerDisabled,
            setHeaderDisabled,
            scrollDirection,
            setScrollDirection,
            showCookies,
            closeCookies,
            openCookies,
            catalog,
        ],
    );
    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

SiteProvider.propTypes = propTypes;

export default SiteContext;
